'use client';

/**
 * DomainBuyPage.tsx — /domener/kjop («Kjøp et domene»)
 *
 * Hero med domenesøk, oversikt, populære endelser, «Registrer i tre steg», funksjoner
 * (akkordion), promo til flytting, FAQ og avsluttende CTA.
 * Innhold: domain-shared.ts + domain-kjop.ts; priser: domain-tlds.ts.
 */

import ClosingCta from '@/components/landing/ClosingCta';
import DomainHeroIllustration from '@/components/domain/DomainHeroIllustration';
import DomainPanelIllustration from '@/components/domain/DomainPanelIllustration';
import DomainSearch from '@/components/domain/DomainSearch';
import DomainShell from '@/components/domain/DomainShell';
import TldCards from '@/components/domain/TldCards';
import FaqAccordion from '@/components/product/FaqAccordion';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import NumberedSteps from '@/components/product/NumberedSteps';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import { DOMAIN_KJOP } from './domain-kjop';
import { DOMAIN_SHARED } from './domain-shared';
import { DOMAIN_POPULAR, DOMAIN_TLDS } from './domain-tlds';

export default function DomainBuyPage() {
  const { locale, setLocale } = useLocale();
  const shared = DOMAIN_SHARED[locale];
  const page = DOMAIN_KJOP[locale];

  return (
    <DomainShell shared={shared} sectionNav={page.sectionNav} locale={locale} onLocaleChange={setLocale}>
      <ProductHero
        hero={page.hero}
        breadcrumb={page.breadcrumb}
        illustration={<DomainHeroIllustration label={page.hero.illustrationLabel} />}
        extra={<DomainSearch search={shared.search} tlds={DOMAIN_TLDS} currency={shared.currency} />}
      />
      <SectionNav nav={page.sectionNav} />

      <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
        <div className={styles.container}>
          <SectionHeader id="overview-heading" eyebrow={page.overview.eyebrow} heading={page.overview.heading} blue>
            {page.overview.body.map((paragraph) => (
              <p key={paragraph} className={styles.lead}>
                {paragraph}
              </p>
            ))}
          </SectionHeader>
        </div>
      </section>

      <TldCards id="endelser" content={page.popular} items={DOMAIN_POPULAR} currency={shared.currency} tag={shared.labels.popular} />
      <NumberedSteps id="steg" steps={page.steps} art={<DomainPanelIllustration label={page.steps.panelLabel} />} />
      <FeatureAccordion features={page.features} art={<DomainPanelIllustration label={page.features.panelLabel} />} />
      <PromoBanner promo={page.promo} />
      <FaqAccordion faq={page.faq} />
      <ClosingCta cta={page.cta} />
    </DomainShell>
  );
}
